import React, { useState, useEffect, useCallback } from 'react';
import {
    X, Loader2, User, Mail, Phone, MapPin, Wallet, Clock, BedDouble, Building2, StickyNote, SlidersHorizontal,
} from 'lucide-react';
import ContactNotesButton from '../../contacts/components/ContactNotesButton';
import ContactLink from '../../contacts/components/ContactLink';
import BuyerPreferencesModal from '../../contacts/components/BuyerPreferencesModal';

const formatTimestamp = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleString('en-AU', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

// Date-only, for the sign-up ("Joined") stamp shown on buyer rows.
const formatDate = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleDateString('en-AU', {
        year: 'numeric', month: 'short', day: 'numeric',
    });
};

// $1,725,000 -> "$1.73M"; $620,000 -> "$620K"
const fmtBudget = (n) => {
    if (n == null) return null;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M`;
    if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
    return `$${n.toLocaleString()}`;
};

const PrefChip = ({ children, color = '#6b7280' }) => (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded"
          style={{ background: `${color}14`, color }}>
        {children}
    </span>
);

// Most recent CRM contact note for a buyer, shown inline in the drill-down so
// the agent sees the latest context without opening the notes popover.
const LatestNote = ({ note }) => {
    if (!note || !note.text) return null;
    return (
        <div className="mt-2 pt-2 border-t border-gray-100">
            <div className="flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-100 px-2 py-1.5">
                <StickyNote className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                    <p className="text-xs text-gray-700 whitespace-pre-wrap break-words">{note.text}</p>
                    {(note.author || note.created_at) && (
                        <p className="text-[10px] text-gray-400 mt-0.5">
                            {note.author}{note.author && note.created_at ? ' · ' : ''}{formatTimestamp(note.created_at)}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

// Per-buyer buying preferences shown under their name/contact in the
// targeting-brief drill-down: suburbs, budget, timeline, finance, beds, types.
const BuyerPreferences = ({ p }) => {
    if (!p) return null;
    const budget = (p.price_min != null || p.price_max != null)
        ? `${fmtBudget(p.price_min) ?? '—'}–${fmtBudget(p.price_max) ?? '—'}`
        : null;
    const hasChips = budget || p.timeline || p.finance
        || p.bedrooms?.length || p.property_types?.length || p.buyer_types?.length;
    if (!p.suburbs?.length && !hasChips) return null;

    return (
        <div className="mt-2 pt-2 border-t border-gray-100 space-y-1.5">
            {p.suburbs?.length > 0 && (
                <div className="flex items-start gap-1.5 text-xs text-gray-600">
                    <MapPin className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span>{p.suburbs.join(', ')}</span>
                </div>
            )}
            {hasChips && (
                <div className="flex flex-wrap items-center gap-1">
                    {budget && <PrefChip color="#0e9f6e"><Wallet className="w-2.5 h-2.5" /> {budget}</PrefChip>}
                    {p.timeline && <PrefChip color="#d97706"><Clock className="w-2.5 h-2.5" /> {p.timeline}</PrefChip>}
                    {p.finance && <PrefChip color="#2563eb">{p.finance}</PrefChip>}
                    {(p.bedrooms || []).map((b) => (
                        <PrefChip key={b} color="#7c3aed"><BedDouble className="w-2.5 h-2.5" /> {b}</PrefChip>
                    ))}
                    {(p.property_types || []).map((t) => (
                        <PrefChip key={t} color="#2563eb"><Building2 className="w-2.5 h-2.5" /> {t}</PrefChip>
                    ))}
                    {(p.buyer_types || []).map((t) => (
                        <PrefChip key={t} color="#6b7280">{t}</PrefChip>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Generic drill-down list for the Engagement tab.
 *
 * variant:
 *  - 'buyers'    rows = {id, name, email, phone, visits?, gap_days?, days_missing?, last_seen?, returned_at?, segment?}
 *  - 'questions' rows = {id, question, name, email, occurred_at, category}
 */
const EngagementListModal = ({ isOpen, onClose, title, subtitle, loading, rows = [], variant = 'buyers' }) => {
    // Local copy so a note added/deleted via ContactNotesButton updates the
    // row's inline note immediately, without waiting for a list refetch.
    const [localRows, setLocalRows] = useState(rows);
    useEffect(() => { setLocalRows(rows); }, [rows]);
    // Buyer whose buying preferences are being edited (null = closed).
    const [prefsIdentity, setPrefsIdentity] = useState(null);

    const handleNoteChange = useCallback((rowId, latestNote) => {
        setLocalRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, latest_note: latestNote } : r)));
    }, []);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 gap-4">
                    <div className="min-w-0">
                        <h2 className="text-xl font-bold text-gray-900 truncate">{title}</h2>
                        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0">
                        <X className="w-6 h-6 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                        </div>
                    ) : localRows.length === 0 ? (
                        <div className="text-center text-gray-500 py-10">No records found in this range.</div>
                    ) : variant === 'questions' ? (
                        <div className="space-y-3">
                            {localRows.map((r) => (
                                <div key={r.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                                    <p className="text-sm text-gray-900 mb-2">“{r.question}”</p>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                                            <span className="inline-flex items-center gap-1">
                                                <User className="w-3.5 h-3.5" />
                                                <ContactLink identity={{ buyerId: r.buyer_id || r.user_id, email: r.email, name: r.name, phone: r.phone }}>
                                                    {r.name}
                                                </ContactLink>
                                            </span>
                                            {r.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {r.email}</span>}
                                            <span>{formatTimestamp(r.occurred_at)}</span>
                                        </div>
                                        <ContactNotesButton
                                            identity={{ buyerId: r.buyer_id || r.user_id, email: r.email, name: r.name, phone: r.phone }}
                                            compact
                                            onNoteChange={(ln) => handleNoteChange(r.id, ln)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : variant === 'churn' ? (
                        <div className="space-y-3">
                            {localRows.map((r) => (
                                <div key={r.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-gray-900">
                                            <User className="w-3.5 h-3.5 text-gray-400" />
                                            <ContactLink identity={{ buyerId: r.buyer_id, email: r.email, name: r.name }}>
                                                {r.name}
                                            </ContactLink>
                                        </span>
                                        <div className="flex items-center gap-2">
                                            {r.flow_label && (
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                                    r.flow === 'deactivate' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                                }`}>
                                                    {r.flow_label}
                                                </span>
                                            )}
                                            <span className="text-xs text-gray-400">{formatTimestamp(r.occurred_at)}</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1">
                                        {r.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {r.email}</span>}
                                    </div>
                                    {(r.reasons || []).length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {r.reasons.map((label, i) => (
                                                <span key={i} className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                                                    {label}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {r.note && (
                                        <div className="mt-2 flex items-start gap-1.5 rounded-md bg-gray-50 border border-gray-100 px-2 py-1.5">
                                            <StickyNote className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                                            <p className="text-xs text-gray-700 whitespace-pre-wrap break-words">{r.note}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {localRows.map((r) => {
                                const hasStats = r.visits != null || r.gap_days != null
                                    || r.days_missing != null || r.returned_at || r.last_seen
                                    || r.opted_in_at || r.signed_up_at;
                                return (
                                    <div key={r.id} className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <ContactLink
                                                        identity={{ buyerId: r.id, email: r.email, name: r.name, phone: r.phone }}
                                                        className="text-sm font-semibold text-gray-900 truncate hover:text-[#3a99a3] hover:underline transition-colors"
                                                    >
                                                        {r.name}
                                                    </ContactLink>
                                                    {r.segment && (
                                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                                            r.segment === 'new' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                                                        }`}>
                                                            {r.segment === 'new' ? 'NEW' : 'RETURNING'}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                                                    {r.email && <span className="inline-flex items-center gap-1 truncate"><Mail className="w-3 h-3" /> {r.email}</span>}
                                                    {r.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone}</span>}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                {hasStats && (
                                                    <div className="text-right">
                                                        {r.visits != null && (
                                                            <div className="text-sm font-bold text-gray-900">{r.visits} visit{r.visits === 1 ? '' : 's'}</div>
                                                        )}
                                                        {r.gap_days != null && (
                                                            <div className="text-sm font-bold text-gray-900">{r.gap_days}d gap</div>
                                                        )}
                                                        {r.days_missing != null && (
                                                            <div className="text-sm font-bold text-gray-900">away {r.days_missing}d</div>
                                                        )}
                                                        {(r.returned_at || r.last_seen) && (
                                                            <div className="text-[11px] text-gray-400">
                                                                {formatTimestamp(r.returned_at || r.last_seen)}
                                                            </div>
                                                        )}
                                                        {r.opted_in_at && r.visits == null
                                                            && r.gap_days == null && r.days_missing == null ? (
                                                            <div className="text-[11px] text-gray-400">
                                                                Added {formatDate(r.opted_in_at)}
                                                            </div>
                                                        ) : r.last_activity_at && r.visits == null
                                                            && r.gap_days == null && r.days_missing == null ? (
                                                            <div className="text-[11px] text-gray-400">
                                                                Last active {formatDate(r.last_activity_at)}
                                                            </div>
                                                        ) : (r.signed_up_at && r.visits == null
                                                            && r.gap_days == null && r.days_missing == null && (
                                                            <div className="text-[11px] text-gray-400">
                                                                Joined {formatDate(r.signed_up_at)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                <ContactNotesButton
                                                    identity={{ buyerId: r.id, email: r.email, name: r.name, phone: r.phone }}
                                                    compact
                                                    onNoteChange={(ln) => handleNoteChange(r.id, ln)}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setPrefsIdentity({ buyerId: r.id, email: r.email, name: r.name, phone: r.phone })}
                                                    title="Edit this buyer's buying preferences"
                                                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-teal-300 hover:text-teal-600"
                                                >
                                                    <SlidersHorizontal className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                        <BuyerPreferences p={r.preferences} />
                                        <LatestNote note={r.latest_note} />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                    <div className="text-sm text-gray-600">
                        Showing {localRows.length} record{localRows.length === 1 ? '' : 's'}
                    </div>
                    <button onClick={onClose} className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
                        Close
                    </button>
                </div>
            </div>
            {prefsIdentity && (
                <BuyerPreferencesModal
                    identity={prefsIdentity}
                    title={prefsIdentity.name}
                    onClose={() => setPrefsIdentity(null)}
                />
            )}
        </div>
    );
};

export default EngagementListModal;
